import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUpload,
  faSparkles,
  faExclamationTriangle,
  faSpinner,
  faCheckCircle,
} from '@fortawesome/pro-solid-svg-icons';
import { Card } from 'react-bootstrap-5';

import Button from 'shared/button';
import { joinWithAnd } from 'helpers/formatters/string_formats';
import { useListingDocumentUpload, ACCEPTED_TYPE_NAMES } from './use_listing_document_upload';

const ACCEPTED_TYPES_FOR_DISPLAY = joinWithAnd(ACCEPTED_TYPE_NAMES);

// Builds the AI ingest metadata object for submission to backend
export function buildIngestMetadata(processedData) {
  return {
    ai_extracted_fields: processedData.propertyFormData || {},
    inferred_fields: processedData.inferredFields || [],
    filename: processedData.uploadMetadata?.filename,
    upload_id: processedData.uploadMetadata?.uploadId,
    ai_model: processedData.uploadMetadata?.aiModel
  };
}

function AiDocumentImport({ saving, onProcessedData, onAiUploadStatusChange }) {
  const handleProcessedData = (processedData) => {
    const { uploadMetadata, ...dataToExpose } = processedData;
    const dataWithMetadata = {
      ...dataToExpose,
      ingestMetadata: buildIngestMetadata(processedData)
    };
    onProcessedData(dataWithMetadata);
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    status,
    fileDropError,
    uploadError,
    processingError,
    selectedFile,
    isUploading,
    progressText,
    resetUpload,
  } = useListingDocumentUpload(saving, handleProcessedData);

  // Notify parent of upload status changes
  // exit confirmation shows only if isUploading is true
  useEffect(() => {
    if (onAiUploadStatusChange) {
      onAiUploadStatusChange(isUploading);
    }
  }, [isUploading, onAiUploadStatusChange]);

  const renderDropzoneContent = () => {
    if (isUploading) {
      return (
        <div className="text-center">
          <div className="text-accent mb-2">
            <FontAwesomeIcon icon={faSpinner} size="2xl" spin />
          </div>
          <p className="fs-large text-center mb-2">
            {progressText}
          </p>
          {selectedFile && (
            <div className="text-muted text-center">{selectedFile.name}</div>
          )}
        </div>
      );
    }

    if (status === 'success') {
      return (
        <div className="text-center">
          <div className="text-success mb-2">
            <FontAwesomeIcon icon={faCheckCircle} size="2xl" />
          </div>
          <p className="fs-large text-center mb-2">
            Document processed successfully!
          </p>
          {selectedFile && (
            <div className="text-muted text-center">{selectedFile.name}</div>
          )}
        </div>
      );
    }

    if (fileDropError) {
      return (
        <>
          <div className="text-center">
            <div className="text-destructive mb-2">
              <FontAwesomeIcon icon={faExclamationTriangle} size="2xl" />
            </div>
            <p className="fs-large text-center text-destructive mb-2">
              {fileDropError}
            </p>
          </div>
          <div className="text-muted text-center mt-2">We support {ACCEPTED_TYPES_FOR_DISPLAY}.</div>
        </>
      );
    }

    if (uploadError || processingError) {
      return (
        <div className="text-center">
          <div className="text-destructive mb-2">
            <FontAwesomeIcon icon={faExclamationTriangle} size="2xl" />
          </div>
          <p className="fs-large text-center text-destructive mb-2">
            {(uploadError &&
               `Upload failed: ${uploadError.message || 'An error occurred during upload'}`) ||
             (processingError &&
               `Processing failed: ${processingError.message ||
                 'An error occurred during processing'}`)}
          </p>
          <Button.Accent
            className="mt-2"
            onClick={(e) => {
              e.stopPropagation();
              resetUpload();
            }}
          >
            Try Again
          </Button.Accent>
        </div>
      );
    }

    return (
      <>
        <div className="text-center text-accent">
          <FontAwesomeIcon icon={faUpload} size="2xl" />
        </div>
        <p className="fs-large text-center my-2">
          Drag or click to upload a listing document
        </p>
        <div className="text-muted text-center">We support {ACCEPTED_TYPES_FOR_DISPLAY}.</div>
      </>
    );
  };

  return (
    <Card className="my-4 bg-buildout-blue-100">
      <Card.Body className="d-flex flex-column gap-3">
        <div className="d-flex align-items-start">
          <FontAwesomeIcon className="me-2 mt-1" icon={faSparkles} />
          <div className="d-flex flex-column">
            <div className="fw-bold">New! AI Document Import</div>
            <div>Our AI will scan your document and automatically fill in key listing details for you.</div>
          </div>
        </div>
        <Card
          className={`border border-info ${isDragActive ? 'bg-buildout-blue-50' : ''}`}
          style={{
            borderStyle: 'dashed',
            transition: 'background 0.2s ease'
          }}
          {...getRootProps()}
        >
          <Card.Body>
            <input
              {...getInputProps({
                multiple: false
              })}
            />
            {renderDropzoneContent()}
          </Card.Body>
        </Card>
      </Card.Body>
    </Card>
  );
}

AiDocumentImport.propTypes = {
  saving: PropTypes.bool.isRequired,
  onAiUploadStatusChange: PropTypes.func,
  onProcessedData: PropTypes.func.isRequired,
};

export default React.memo(AiDocumentImport);
